<template>
	<div class="input-container">
		<header :class="[
            'input-header',
            {
                'is-small': isSmall,
                'sr-only': hideLabel,
            }
        ]">
			<label :class="{ 'has-error': error }" :for="inputId" data-test-label>
				{{ label }}
				<span v-if="isOptional && !disabled" data-test-optional>(OPTIONAL)</span>
			</label>

			<OaoTooltip
				v-if="tooltip && filteredInputConfig"
				:description="filteredInputConfig.helpText"
				:title="filteredInputConfig.helpTextTitle"
				isSmall
			/>
		</header>

		<div class="input-control-container">
			<input
				:id="inputId"
				:class="[
					'date-input',
					{
                        'is-small': isSmall,
						'has-error': error,
						'sm_cobrowsing_masked_field': gliaMask,
						'sm_cobrowsing_disabled_field': gliaDisable
					}
				]"
				:name="label"
				type="date"
				:placeholder="placeholder"
				v-model="innerValue"
				:disabled="disabled"
				:min="minDateValue"
				:max="maxDateValue"
				:autocomplete="disableAutocomplete ? 'off' : 'on'"
				data-test-input
				@change="$emit('change', $event)"
				@blur="focused = false"
				@focus="focused = true"
			/>
            <slot name="input-control" />
		</div>

        <p class="input-sublabel" v-if="sublabel">

            <span class="sublabel-warning" v-if="sublabelWarning">⚠</span>

            {{ sublabel }}
        </p>

		<transition name="fade">
			<p class="has-error" v-if="error" data-test-error>
				{{ error }}
			</p>
		</transition>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import OaoTooltip from '@/components/OaoTooltip';

export default {
	name: 'OaoDateInput',
	components: {
		OaoTooltip
	},
	data() {
		return {
			focused: false
		};
	},
	props: {
		value: {
			type: String,
			required: true
		},
		label: {
			type: String,
			required: true
		},
		placeholder: {
			type: String,
			default: 'MM/DD/YYYY'
		},
		disabled: {
			type: Boolean,
			default: false
		},
		error: {
			type: String,
			default: ''
		},
		isOptional: {
			type: Boolean,
			default: false
		},
		disableAutocomplete: {
			type: Boolean,
			default: false
		},
        isSmall: {
            type: Boolean,
            required: false,
            default: false,
        },
		tooltip: {
			type: Boolean,
			default: false
		},
		tooltipTypeId: {
			type: Number,
			default: null
		},
        hideLabel: {
            type: Boolean,
            required: false,
        },
        sublabel: {
            type: String,
            required: false,
        },
        sublabelWarning: {
            type: Boolean,
            required: false,
        },
		minDate: {
			type: String,
			default: ''
		},
		maxDate: {
			type: String,
			default: ''
		},
		gliaMask: {
			type: Boolean,
			default: false
		},
		gliaDisable: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		...mapGetters('config', ['getInputConfig']),
		inputId() {
			return `${this.label.replace(/\s/g, '')}-${this._uid}`;
		},
		filteredInputConfig() {
			return this.getInputConfig(this.tooltipTypeId);
		},
		minDateValue() {
			return this.convertToISODate(this.minDate);
		},
		maxDateValue() {
			return this.convertToISODate(this.maxDate);
		},
		innerValue: {
			get() {
				// Convert MM/DD/YYYY to YYYY-MM-DD for native date input
				if (!this.value) return '';

				if (this.value.includes('/')) {
					const parts = this.value.split('/');
					if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
						return `${parts[2]}-${parts[0]}-${parts[1]}`;
					}
				}

				return this.value;
			},
			set(val) {
				// Convert YYYY-MM-DD to MM/DD/YYYY for emission
				if (!val) {
					this.$emit('input', '');
					return;
				}

				if (val.includes('-')) {
					const parts = val.split('-');
					if (parts.length === 3) {
						const formattedDate = `${parts[1]}/${parts[2]}/${parts[0]}`;
						this.$emit('input', formattedDate);
						return;
					}
				}

				this.$emit('input', val);
			}
		}
	},
	watch: {
		focused(newVal) {
			if (newVal) {
				this.$emit('focus', newVal);
			} else {
				this.$emit('blur', newVal);
			}
		}
	},
	methods: {
		convertToISODate(dateString) {
			if (!dateString) return '';

			// If already in YYYY-MM-DD format
			if (dateString.includes('-')) {
				return dateString;
			}

			// Convert MM/DD/YYYY to YYYY-MM-DD
			if (dateString.includes('/')) {
				const parts = dateString.split('/');
				if (parts.length === 3) {
					return `${parts[2]}-${parts[0]}-${parts[1]}`;
				}
			}

			return '';
		}
	}
};
</script>

<style lang="scss" scoped>
.input-container {
	&:not(:last-child) {
		margin-bottom: 1rem;
	}

	.has-error {
		color: var(--danger);
	}

	.input-header {
		display: flex;
		flex-direction: row;
		gap: 0.5rem;
		align-items: baseline;

		label {
			display: block;
			color: var(--text-primary);
			font-size: 0.8rem;
			margin-bottom: 0.5rem;
			font-weight: 400;
			text-transform: uppercase;
		}
	}

	input.date-input {
		box-sizing: border-box;
		width: 100%;
		border: none;
		border-bottom: 1.5px solid rgb(215, 215, 215);
		background-color: transparent;
		font-size: 1.25rem;
		padding: 0.25rem;
		font-family: sans-serif;
		font-weight: normal;
		color: inherit;

		&::placeholder {
			color: var(--lightGrey);
			font-style: oblique;
		}

		&.has-error {
			border-bottom: 1.5px solid var(--danger);
		}

		&:disabled {
			color: var(--lightGrey);
			cursor: not-allowed;
		}

		// Style the native calendar icon
		&::-webkit-calendar-picker-indicator {
			cursor: pointer;
			opacity: 0.6;
			transition: opacity 0.2s ease;

			&:hover {
				opacity: 1;
			}
		}

		&:disabled::-webkit-calendar-picker-indicator {
			cursor: not-allowed;
			opacity: 0.3;
		}

		// Firefox date input styling
		&::-moz-calendar-picker-indicator {
			cursor: pointer;
			opacity: 0.6;
			transition: opacity 0.2s ease;

			&:hover {
				opacity: 1;
			}
		}
	}

    p {
        font-size: 0.85rem;
        margin-top: 0.25rem;
    }

    .is-small {
        width: auto;
        min-width: 12rem;
    }

    .force-capitals {
        text-transform: uppercase;
    }

    .input-sublabel {
        font-style: italic;
        font-size: 0.7rem;
        line-height: 1rem;

        .sublabel-warning {
            font-style: normal;
            color: orange;
        }
    }
}

.input-control-container {
	display: flex;
	flex-direction: row;
	align-content: space-between;
}

@media only screen and (max-width: 850px) {
    ::v-deep .input-control-container {
        flex-direction: column;
        gap: 1rem;

        button {
            margin: 0px;
        }
    }
}
</style>
